import {createSlice} from '@reduxjs/toolkit';
import moment from 'moment';
import {daysBetweenDates, stringToDate} from '../../utils/dates';
import {firstKey, isEmpty, lastKey} from '../../utils/objects';
import {scheduleWindowsApi} from '../api/scheduleWindowsApiSlice';

const initialState = {
  end: null,
  minimumDay: moment().toISOString(),
  preciseTimes: false,
  schedulableWindows: {},
  selectedDay: null, //selectedDay is the day that the user has selected
  targetDay: moment().toISOString(), //targetDay is the day we need to fetch windows for
  start: null,
  utcOffset: moment().utcOffset(),
  validatingWindow: false,
  errors: [],
  neededDays: 14,
  hqSettingsNeededDays: 14, //number_of_days_to_fetch_for_customer_schedule value from HQ settings
  loading: false,
  appointmentId: null,
};

// Times need to be stored as ISO strings, not moments, because moments are not serializable objects and therefore should not be stored in Redux state.
const schedulerSlice = createSlice({
  name: 'scheduler',
  initialState,
  reducers: {
    setHqSettingsNeededDays(state, action) {
      // This action should only ever be dispatched once after fetching HQ settings in App.jsx
      const {hqSettingsNeededDays} = action.payload;

      if (hqSettingsNeededDays) {
        state.hqSettingsNeededDays = hqSettingsNeededDays;
        state.neededDays = hqSettingsNeededDays;
      }
    },
    daySelected(state, action) {
      const {day} = action.payload;

      state.selectedDay = day;
    },
    timeWindowSelected(state, action) {
      const {start, end, utcOffset, appointmentId} = action.payload;

      state.start = start;
      state.end = end;
      state.utcOffset = utcOffset;
      state.appointmentId = appointmentId;
    },
    monthDecremented(state) {
      const newDay = moment(state.selectedDay).subtract(1, 'month').startOf('month').toISOString();

      state.selectedDay = newDay;
    },
    monthIncremented(state, action) {
      const {
        windowsData: {windows},
        timeZone,
      } = action.payload;
      const newDay = moment.tz(state.selectedDay, timeZone).add(1, 'month').startOf('month').toISOString();
      const endOfSelectedMonth = moment(newDay).endOf('month');
      const lastStoredDate = stringToDate(lastKey(windows));
      const neededDays = daysBetweenDates(lastStoredDate, endOfSelectedMonth);
      const newTargetDay = isEmpty(windows)
        ? newDay
        : moment.tz(lastKey(windows), timeZone).add(2, 'day').toISOString();

      state.selectedDay = newDay;
      state.neededDays = neededDays;
      state.targetDay = newTargetDay;
    },
    weekDecremented(state) {
      const newDay = moment(state.selectedDay).subtract(1, 'week').day(moment(state.selectedDay).day()).toISOString();

      state.selectedDay = newDay;
    },
    weekIncremented(state, action) {
      const {
        windowsData: {windows, minimumDay},
        timeZone,
      } = action.payload;
      const newDay = moment.tz(state.selectedDay, timeZone).add(1, 'week');

      const newTargetDay = isEmpty(windows) ? newDay : moment.tz(lastKey(windows), timeZone).add(1, 'day');
      const minimumDayMoment = moment(minimumDay);
      state.selectedDay = newDay.toISOString();

      if (!areFirstTwoWeekDatesCompletelyStored(minimumDayMoment, windows)) {
        const firstStoredDate = stringToDate(firstKey(windows));
        const neededDays = daysBetweenDates(firstStoredDate, minimumDayMoment) + state.hqSettingsNeededDays;
        state.neededDays = neededDays;
        state.targetDay = newTargetDay.toISOString();
      } else if (!areNextWeekDatesAlreadyStored(newDay, windows)) {
        state.neededDays = state.hqSettingsNeededDays;
        state.targetDay = newTargetDay.toISOString();
      }
    },
    schedulingLoading(state) {
      state.loading = true;
    },
    schedulingLoaded(state) {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // for auto-selecting first day with available time windows out of newest windows fetched
    builder.addMatcher(scheduleWindowsApi.endpoints.getSchedulableWindows.matchFulfilled, (state, action) => {
      const {selectedDay} = action.payload;
      if (state.selectedDay === null) {
        state.selectedDay = selectedDay;
      }
    });
  },
});

const areFirstTwoWeekDatesCompletelyStored = (minimumDay, schedulableWindows) => {
  const dateInTwoWeeks = minimumDay.clone().add(13, 'days');
  return schedulableWindows[dateInTwoWeeks.format('YYYY-MM-DD')] !== undefined;
};

const areNextWeekDatesAlreadyStored = (day, schedulableWindows) => {
  return schedulableWindows[day.format('YYYY-MM-DD')] !== undefined;
};

// `createSlice` automatically generated action creators with these names.
export const {
  daySelected,
  timeWindowSelected,
  monthDecremented,
  monthIncremented,
  weekDecremented,
  weekIncremented,
  schedulingLoading,
  schedulingLoaded,
  setHqSettingsNeededDays,
} = schedulerSlice.actions;

// Export the slice reducer as the default export
export default schedulerSlice.reducer;
